import { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Spinner,
} from 'reactstrap';
import { Btn } from '../../../../AbstractElements';
import { toast } from 'react-toastify';
import UserService from '../../../../Api/services/user.service';
import RoleService from '../../../../Api/services/role.service';
import CountryService from '../../../../Api/services/country.service';
import type { components } from '../../../../Api/types/openapi';
import { useTranslation } from 'react-i18next';

type UserSimpleResponse = components['schemas']['UserSimpleResponse'];
type UserResponse = components['schemas']['UserResponse'];
type RoleResponse = components['schemas']['RoleResponse'];
type CountryResponse = components['schemas']['CountryResponse'];
type CreateUser = components['schemas']['CreateUser'];
type UpdateUser = components['schemas']['UpdateUser'];

interface UserFormModalProps {
  isOpen: boolean;
  toggle: () => void;
  user: UserSimpleResponse | null;
  isEditing: boolean;
  onSuccess: () => void;
}

const UserFormModal: React.FC<UserFormModalProps> = ({
  isOpen,
  toggle,
  user,
  isEditing,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [roles, setRoles] = useState<RoleResponse[]>([]);
  const [countries, setCountries] = useState<CountryResponse[]>([]);
  const [userData, setUserData] = useState<UserResponse | null>(null);

  // Form state
  const [formData, setFormData] = useState<Partial<CreateUser>>({
    name: '',
    email: '',
    password: '',
    phone: '',
    country_code_phone: '',
    role: 0,
    country: null,
    language: 'ES',
  });

  useEffect(() => {
    if (isOpen) {
      loadFormData();
    }
  }, [isOpen, user]);

  const loadFormData = async () => {
    setLoadingData(true);
    try {
      // Cargar roles y países
      const [rolesData, countriesData] = await Promise.all([
        RoleService.getAllActiveRoles(),
        CountryService.getAllActiveCountries(),
      ]);
      setRoles(rolesData);
      setCountries(countriesData);

      // Si es edición, cargar datos del usuario
      if (isEditing && user?.id) {
        const userData = await UserService.getUserById(user.id);
        setUserData(userData);
        setFormData({
          name: userData.name || '',
          email: userData.email || '',
          password: '', // No cargar contraseña
          phone: userData.phone || '',
          country_code_phone: userData.country_code_phone || '',
          role: userData.role?.id || 0,
          country: userData.country?.id || null,
          language: userData.language || 'ES',
        });
      } else {
        // Reset form for new user - todos los campos vacíos
        setFormData({
          name: '',
          email: '',
          password: '',
          phone: '',
          country_code_phone: '',
          role: 0,
          country: null,
          language: 'ES',
        });
        setUserData(null);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al cargar datos');
      toast.error(errorMessage);
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditing && userData) {
        // Actualizar usuario
        const updateData: UpdateUser = {
          id: userData.id,
          name: formData.name || '',
          email: formData.email || '',
          username: userData.username || null, // Mantener el username existente al actualizar
          phone: formData.phone || '',
          country_code_phone: formData.country_code_phone || '',
          role: formData.role || 0,
          country: formData.country || null,
          language: formData.language || 'ES',
        };

        await UserService.updateUser(updateData);
        toast.success(t('Usuario actualizado exitosamente'));
      } else {
        // Crear usuario
        if (!formData.password) {
          toast.error(t('La contraseña es requerida'));
          setLoading(false);
          return;
        }

        if (!formData.email || formData.email.trim() === '') {
          toast.error(t('El email es requerido'));
          setLoading(false);
          return;
        }

        if (!formData.role || formData.role === 0) {
          toast.error(t('El rol es requerido'));
          setLoading(false);
          return;
        }

        // Generar username automáticamente desde el email
        // Tomar la parte antes del @ y limpiar caracteres especiales
        const emailPart = formData.email.split('@')[0];
        const generatedUsername = emailPart
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '') // Remover caracteres especiales
          .substring(0, 30); // Limitar longitud

        if (!generatedUsername || generatedUsername === '') {
          toast.error(t('No se pudo generar un nombre de usuario válido desde el email'));
          setLoading(false);
          return;
        }

        const createData: CreateUser = {
          name: formData.name || '',
          email: formData.email || '',
          password: formData.password || '',
          username: generatedUsername,
          phone: formData.phone || '',
          country_code_phone: formData.country_code_phone || '',
          role: formData.role || 0,
          country: formData.country || null,
          language: formData.language || 'ES',
        };

        await UserService.createUser(createData);
        toast.success(t('Usuario creado exitosamente'));
      }

      onSuccess();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        t('Error al guardar usuario');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'role' || name === 'country' ? (value ? parseInt(value) : null) : value,
    }));
  };

  if (loadingData) {
    return (
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>
          {isEditing ? t('Editar Usuario') : t('Nuevo Usuario')}
        </ModalHeader>
        <ModalBody>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
            <Spinner color="primary" />
          </div>
        </ModalBody>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <Form onSubmit={handleSubmit}>
        <ModalHeader toggle={toggle}>
          {isEditing ? t('Editar Usuario') : t('Nuevo Usuario')}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>{t('Nombre')} *</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>{t('Email')} *</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email || ''}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label>{isEditing ? t('Nueva Contraseña') : t('Contraseña')} *</Label>
                <Input
                  type="password"
                  name="password"
                  value={formData.password || ''}
                  onChange={handleChange}
                  required={!isEditing}
                  placeholder={isEditing ? t('Dejar vacío para no cambiar') : ''}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="4">
              <FormGroup>
                <Label>{t('Código País Teléfono')} *</Label>
                <Input
                  type="text"
                  name="country_code_phone"
                  value={formData.country_code_phone || ''}
                  onChange={handleChange}
                  required
                  placeholder="+1"
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label>{t('Teléfono')} *</Label>
                <Input
                  type="text"
                  name="phone"
                  value={formData.phone || ''}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label>{t('País')}</Label>
                <Input
                  type="select"
                  name="country"
                  value={formData.country || ''}
                  onChange={handleChange}
                >
                  <option value="">{t('Seleccionar país')}</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label>{t('Rol')} *</Label>
                <Input
                  type="select"
                  name="role"
                  value={formData.role || 0}
                  onChange={handleChange}
                  required
                >
                  <option value="">{t('Seleccionar rol')}</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>{t('Idioma')} *</Label>
                <Input
                  type="select"
                  name="language"
                  value={formData.language || 'ES'}
                  onChange={handleChange}
                  required
                >
                  <option value="ES">{t('Español')}</option>
                  <option value="EN">{t('English')}</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Btn color="secondary" onClick={toggle} disabled={loading}>
            {t('Cancelar')}
          </Btn>
          <Btn color="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t('Guardando...')}
              </>
            ) : (
              t('Aceptar')
            )}
          </Btn>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default UserFormModal;

